import { Route } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Radio } from "antd";
import { gql } from "graphql-request";
import { graphQLClient } from "src/modules/tab/components/PublishTabToProd";
import { getLastStringFromUrl } from "src/helpers";
import { useParams } from "react-router-dom";
import axios from "axios";
import { handleInvalidateCache } from "../generic-data/updateGenericData";

const formatContextId = (id: string) => {
    return `personalizedLearning|worksheetCollection|${id}`;
};

const exampleJsonData = {
    icon: "",
    tittle: "",
    worksheetIds: [191829, 232322, 232323, 343434, 343434, 343566],
};

const extractOriginalId = (contextId: string) => {
    return contextId.split("|")[2] || "";
};

const FETCH_DATA_QUERY = gql`
    query getGenericData($id: String!) {
        other_generic_data(where: { context_id: { _eq: $id } }) {
            context_id
            data
            id
        }
    }
`;

const UPDATE_DATA_MUTATION = gql`
    mutation UpdateGenericData($object: other_generic_data_insert_input = {}) {
        insert_other_generic_data_one(
            object: $object
            on_conflict: {
                constraint: generic_data_context_id_key
                update_columns: [data, model]
            }
        ) {
            id
            context_id
            data
        }
    }
`;

const CREATE_DATA_MUTATION = gql`
    mutation CreateGenericData($object: other_generic_data_insert_input!) {
        insert_other_generic_data_one(object: $object) {
            id
            context_id
            data
        }
    }
`;

const UpdateSharePage = () => {
    const [form] = Form.useForm();
    const { id } = useParams();
    const [data, setData] = useState<any>();
    const [contextId, setContextId] = useState<any>("");
    const [displayId, setDisplayId] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [dataExists, setDataExists] = useState<boolean>(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;

            try {
                const formattedContextId = formatContextId(id);
                let existingData = null;

                // First try fetching from Firestore if enabled
                if (process.env.REACT_APP_SYNC_TO_FIRESTORE === "true") {
                    try {
                        const firestoreResponse = await axios.post(
                            `${process.env.REACT_APP_HOMEWORK_SERVER_API_ENDPOINT}/v3/personalizedLearning/syncOtherGenericDataWithFirestore`,
                            {
                                data: {
                                    type: "get",
                                    contextId: formattedContextId,
                                },
                            },
                            { headers: { "Content-Type": "application/json" } },
                        );

                        if (
                            firestoreResponse?.data?.success &&
                            firestoreResponse?.data?.data
                        ) {
                            existingData = firestoreResponse.data.data;
                        }
                    } catch (err) {
                        console.warn(
                            "Firestore fetch failed, falling back to GraphQL:",
                            err,
                        );
                        // Do nothing here — fallback will handle it
                    }
                }

                // Fallback to GraphQL if Firestore didn't return data
                if (!existingData) {
                    try {
                        const response: any = await graphQLClient.request(
                            FETCH_DATA_QUERY,
                            {
                                id: formattedContextId,
                            },
                        );
                        existingData = response?.other_generic_data?.[0];
                    } catch (err) {
                        console.error("GraphQL fetch failed:", err);
                        message.error(
                            `Error fetching fallback data: ${err.message}`,
                        );
                        return;
                    }
                }

                if (existingData) {
                    setData(JSON.stringify(existingData.data ?? [], null, 2));
                    setContextId(existingData.context_id || formattedContextId);
                    setDisplayId(
                        extractOriginalId(
                            existingData.context_id || formattedContextId,
                        ),
                    );
                    setDataExists(true);
                } else {
                    setContextId(formattedContextId);
                    setDisplayId(id);
                    setDataExists(false);
                }
            } catch (error: any) {
                console.error("Unexpected error:", error);
                message.error(`Error fetching data: ${error.message}`);
            }
        };

        fetchData();
    }, [id]);

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // Step 1: Validate JSON
            let parsedData;
            try {
                parsedData = JSON.parse(data);
            } catch (error) {
                message.error("Invalid JSON format");
                return;
            }

            const dataObject = {
                context_id: contextId,
                data: {
                    collectionSlug: extractOriginalId(contextId),
                    ...parsedData,
                },
                model: null,
            };

            // Step 2: Sync to Firestore if enabled
            if (process.env.REACT_APP_SYNC_TO_FIRESTORE === "true") {
                try {
                    await axios.post(
                        `${process.env.REACT_APP_HOMEWORK_SERVER_API_ENDPOINT}/v3/personalizedLearning/syncOtherGenericDataWithFirestore`,
                        {
                            data: {
                                type: "set",
                                contextId: contextId,
                                payload: dataObject.data,
                            },
                        },
                        { headers: { "Content-Type": "application/json" } },
                    );
                } catch (err: any) {
                    console.error("Firestore sync error:", err);
                    message.error(`Firestore sync failed: ${err.message}`);
                }
            }

            // Step 3: GraphQL update or create
            try {
                if (dataExists) {
                    await graphQLClient.request(UPDATE_DATA_MUTATION, {
                        object: dataObject,
                    });
                    message.success("Data updated successfully");
                } else {
                    await graphQLClient.request(CREATE_DATA_MUTATION, {
                        object: dataObject,
                    });
                    message.success("Data created successfully");
                    setDataExists(true);
                }
                handleInvalidateCache(contextId);
            } catch (err: any) {
                console.error("GraphQL mutation error:", err);
                message.error(
                    `Error ${dataExists ? "updating" : "creating"} data: ${
                        err.message
                    }`,
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const loadExampleData = () => {
        setData(JSON.stringify(exampleJsonData, null, 2));
    };
    return (
        <div
            style={{
                padding: 20,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100dvh",
            }}
        >
            <Form
                layout="vertical"
                form={form}
                className="w-full border"
                style={{ width: "75%" }}
            >
                <h1 className="text-2xl font-bold mb-6">
                    📮 Worksheet's Collection Page Config
                </h1>

                <Form.Item label="Worksheet Collection Slug">
                    <Input
                        placeholder="Collection Slug"
                        disabled
                        value={displayId}
                    />
                </Form.Item>

                <Form.Item
                    label="Data in JSON Format"
                    help="Enter valid JSON data"
                    rules={[
                        {
                            validator: async (_, value) => {
                                try {
                                    if (value) JSON.parse(value);
                                    return Promise.resolve();
                                } catch (error) {
                                    return Promise.reject(
                                        "Please enter valid JSON",
                                    );
                                }
                            },
                        },
                    ]}
                >
                    <Input.TextArea
                        placeholder="Enter JSON data here"
                        rows={10}
                        value={data}
                        onChange={(e) => setData(e.target.value)}
                    />
                </Form.Item>

                <Form.Item>
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={loading}
                        style={{ marginRight: "10px" }}
                    >
                        {dataExists ? "Update" : "Create"}
                    </Button>
                    {!dataExists && (
                        <Button type="primary" onClick={loadExampleData}>
                            Load Example JSON Data
                        </Button>
                    )}
                </Form.Item>
            </Form>
        </div>
    );
};

export default UpdateSharePage;

export const SHARE_PAGE_ROUTE = [
    <Route
        key="worksheet_collection"
        path="/wc/:id"
        element={<UpdateSharePage />}
    />,
];
